/**
 * v-resizable — adds drag handles so users can adjust an element's height,
 * width, or both, persisting the chosen size to localStorage.
 *
 * Usage:
 *   v-resizable="{ key: 'pie', minW: 480, minH: 320, maxH: 1000 }"
 *   v-resizable="'report-123'"   // shorthand for { key: 'report-123' }
 *
 * Width is applied via the width property (capped at 100%) so resized blocks
 * can flow side by side in the flex-wrap page layout. Added for TT-6716.
 */
import store from '@/store';

const STORAGE_PREFIX = 'FYGR_ANALYTICS_SIZE_';

function getOptions(binding) {
  const v = typeof binding.value === 'string' ? {key: binding.value} : binding.value || {};
  return {
    key: v.key,
    minW: v.minW ?? 320,
    minH: v.minH ?? 280,
    maxH: v.maxH ?? 1000,
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function readSize(key) {
  if (!key) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_PREFIX + key));
    // Ignore any legacy/non-object value so we never write props onto a number.
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSize(key, size) {
  if (key) localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(size));
}

function applyWidth(el, w) {
  el.style.width = `${w}px`;
  el.style.maxWidth = '100%';
}

function makeHandle(className, title) {
  const handle = document.createElement('div');
  handle.className = `fy-resize-handle ${className}`;
  handle.title = title;
  return handle;
}

export const resizable = {
  mounted(el, binding) {
    const opts = getOptions(binding);
    const {key, minW, minH, maxH} = opts;
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }

    // Restore a previously saved size.
    const saved = readSize(key);
    if (Number.isFinite(saved.h)) el.style.height = `${clamp(saved.h, minH, maxH)}px`;
    if (Number.isFinite(saved.w)) applyWidth(el, saved.w);

    const handles = {
      bottom: makeHandle('bottom', 'Glisser pour ajuster la hauteur'),
      right: makeHandle('right', 'Glisser pour ajuster la largeur'),
      corner: makeHandle('corner', 'Glisser pour redimensionner'),
    };
    Object.values(handles).forEach((h) => el.appendChild(h));

    let mode = null; // 'h' | 'w' | 'both'
    let startX = 0;
    let startY = 0;
    let startW = 0;
    let startH = 0;
    let maxW = Infinity;
    let overlay = null;
    let notifiedMin = false;
    let rafId = 0;

    const CURSORS = {h: 'ns-resize', w: 'ew-resize', both: 'nwse-resize'};

    // amCharts' own ResizeObserver gets starved during a continuous drag, so
    // its SVG keeps its old pixel size. Emit a throttled event the charts
    // listen to, to force a re-measure on each frame.
    function scheduleResizeNotify() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        window.dispatchEvent(new CustomEvent('fy-analytics-resize'));
      });
    }

    // Let the user know (once per drag) why the block stops shrinking.
    function notifyMinReached() {
      if (notifiedMin) return;
      notifiedMin = true;
      store.dispatch('app/actionAddNotification', {
        msg: 'Taille minimale atteinte : ce graphique ne peut pas être réduit davantage.',
        color: 'gray',
        disappearing: true,
        once: true,
      });
    }

    // amCharts captures pointer events over its SVG, so a transparent overlay
    // guarantees we keep receiving mouse moves while dragging over a chart.
    function addOverlay(cursor) {
      overlay = document.createElement('div');
      overlay.style.cssText =
        'position:fixed;inset:0;z-index:99999;' + `cursor:${cursor};`;
      document.body.appendChild(overlay);
    }
    function removeOverlay() {
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      overlay = null;
    }

    const onMove = (e) => {
      if (!mode) return;
      let belowMin = false;
      if (mode === 'w' || mode === 'both') {
        const rawW = startW + (e.clientX - startX);
        if (rawW < minW) belowMin = true;
        applyWidth(el, clamp(rawW, minW, maxW));
      }
      if (mode === 'h' || mode === 'both') {
        const rawH = startH + (e.clientY - startY);
        if (rawH < minH) belowMin = true;
        el.style.height = `${clamp(rawH, minH, maxH)}px`;
      }
      if (belowMin) notifyMinReached();
      scheduleResizeNotify();
    };

    const onUp = () => {
      if (!mode) return;
      const ended = mode;
      mode = null;
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      document.body.style.userSelect = '';
      removeOverlay();
      // Persist only the axis (axes) actually dragged, keeping the rest.
      const rect = el.getBoundingClientRect();
      const size = readSize(key);
      if (ended === 'w' || ended === 'both') size.w = Math.round(rect.width);
      if (ended === 'h' || ended === 'both') size.h = Math.round(rect.height);
      writeSize(key, size);
      // Final re-measure once the drag settles.
      window.dispatchEvent(new CustomEvent('fy-analytics-resize'));
    };

    const startDrag = (nextMode) => (e) => {
      mode = nextMode;
      notifiedMin = false;
      startX = e.clientX;
      startY = e.clientY;
      const rect = el.getBoundingClientRect();
      startW = rect.width;
      startH = rect.height;
      const parent = el.parentElement;
      maxW = parent ? parent.clientWidth : rect.width;
      document.body.style.userSelect = 'none';
      addOverlay(CURSORS[nextMode]);
      // Capture phase + overlay so chart libraries can't swallow the moves.
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
      e.preventDefault();
      e.stopPropagation();
    };

    handles.bottom.addEventListener('mousedown', startDrag('h'));
    handles.right.addEventListener('mousedown', startDrag('w'));
    handles.corner.addEventListener('mousedown', startDrag('both'));

    el._fyResizeCleanup = () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      if (rafId) cancelAnimationFrame(rafId);
      removeOverlay();
    };
  },
  unmounted(el) {
    if (el._fyResizeCleanup) el._fyResizeCleanup();
  },
};
